import Image from "next/image"
import Link from "next/link"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

export interface AddOnCardProps {
  title: string
  description: string
  price: string
  /** Optional member price shown in green after the regular price
   *  (e.g. "CHF 139"). Rendered as "· CHF 139 {memberLabel}". */
  memberPrice?: string
  /** Translated suffix after the member price (e.g. "for members"). */
  memberLabel?: string
  imageSrc?: string
  href?: string
  /** Optional status pill rendered inline next to the title (e.g. the
   *  CMS-driven "Not available" pill for a service `not_available` in-market). */
  badge?: ReactNode
  /** Grey the card out (a service `not_available` in-market). */
  muted?: boolean
  className?: string
}

export function AddOnCard({
  title,
  description,
  price,
  memberPrice,
  memberLabel,
  imageSrc,
  href,
  badge,
  muted,
  className,
}: AddOnCardProps) {
  const content = (
    <>
      {imageSrc && (
        <Image
          src={imageSrc}
          alt={title}
          width={64}
          height={64}
          className={cn(
            "flex-shrink-0 rounded-[12px] object-cover",
            muted && "opacity-60 grayscale"
          )}
        />
      )}
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p
            className={cn(
              "type-title-md",
              muted ? "text-neutral-500" : "text-neutral-900"
            )}
          >
            {title}
          </p>
          {badge}
        </div>
        <p
          className={cn(
            "type-body-md",
            muted ? "text-neutral-400" : "text-neutral-500"
          )}
        >
          {description}
        </p>
        <p
          className={cn(
            "type-title-sm mt-1",
            muted ? "text-neutral-500" : "text-neutral-900"
          )}
        >
          {price}
          {memberPrice && (
            <span className="text-deep-green-500 font-normal">
              {" · "}
              {memberPrice}
              {memberLabel ? ` ${memberLabel}` : ""}
            </span>
          )}
        </p>
      </div>
    </>
  )

  const styles = cn(
    "flex items-start gap-4 p-4 bg-white rounded-[16px] transition-shadow duration-300 ease-out",
    // Same soft lift as PricingCard on hover, just a smaller spread to
    // match the add-on card's lower profile (24px blur vs the pricing
    // card's 32px). Single layer, negative spread keeps the edge clean.
    href &&
      "cursor-pointer hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.08)]",
    className
  )

  if (href) {
    return (
      <Link href={href} className={styles}>
        {content}
      </Link>
    )
  }
  return <div className={styles}>{content}</div>
}
